import { Box, Flex, Text } from "@chakra-ui/react";
import { crm } from "../../../../theme";

export const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
export const brl0 = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
export const num = new Intl.NumberFormat("pt-BR");
export const pctFmt = (v: number, digits = 1) =>
  `${v.toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;

export const fmtDate = (iso?: string | null) => {
  if (!iso) return "–";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

export const monthLabel = (ym: string) => {
  const [y, m] = ym.split("-");
  const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${names[Number(m) - 1]}/${y.slice(2)}`;
};

export const KpiTile = ({ title, value, hint }: { title: string; value: string | number; hint?: string }) => (
  <Box bg={crm.surface} borderRadius="16px" p="20px" boxShadow={crm.shadowSm} border={`1px solid ${crm.border}`}>
    <Text fontSize="sm" color={crm.textMuted} fontWeight="500" mb="8px">
      {title}
    </Text>
    <Text fontSize="24px" fontWeight="700" color={crm.text} lineHeight="1.1" wordBreak="break-word">
      {value}
    </Text>
    {hint && (
      <Text fontSize="xs" color={crm.textFaint} mt="6px">
        {hint}
      </Text>
    )}
  </Box>
);

export const Panel = ({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box bg={crm.surface} borderRadius="16px" p="20px" boxShadow={crm.shadowSm} border={`1px solid ${crm.border}`} minW={0}>
    <Flex justify="space-between" align="flex-start" mb="14px" gap="8px" flexWrap="wrap">
      <Box>
        <Text fontWeight="700" color={crm.text}>
          {title}
        </Text>
        {subtitle && (
          <Text fontSize="xs" color={crm.textMuted}>
            {subtitle}
          </Text>
        )}
      </Box>
      {action}
    </Flex>
    {children}
  </Box>
);

export const EmptyState = ({ title, hint }: { title: string; hint?: string }) => (
  <Flex direction="column" align="center" justify="center" py="32px" textAlign="center">
    <Text color={crm.textMuted} fontWeight="600">
      {title}
    </Text>
    {hint && (
      <Text color={crm.textFaint} fontSize="sm" mt="4px">
        {hint}
      </Text>
    )}
  </Flex>
);

export const NO_DATA_HINT = "Importe os dados em Configurações › Importações.";

// Barra horizontal simples (ranking).
export const RankBar = ({
  label,
  value,
  max,
  display,
}: {
  label: string;
  value: number;
  max: number;
  display: string;
}) => (
  <Box mb="10px">
    <Flex justify="space-between" gap="8px" mb="3px">
      <Text fontSize="sm" color={crm.text} noOfLines={1} title={label}>
        {label}
      </Text>
      <Text fontSize="sm" color={crm.textMuted} whiteSpace="nowrap">
        {display}
      </Text>
    </Flex>
    <Box h="8px" bg={crm.columnBg} borderRadius="full">
      <Box h="8px" w={`${max > 0 ? Math.max(2, (value / max) * 100) : 0}%`} bg={crm.brand} borderRadius="full" />
    </Box>
  </Box>
);

export const selectStyle = {
  height: "36px",
  border: `1px solid ${crm.border}`,
  borderRadius: "8px",
  padding: "0 10px",
  background: crm.surface,
  color: crm.text,
  fontSize: "14px",
  minWidth: "140px",
} as React.CSSProperties;

export const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="xs" color={crm.textMuted} fontWeight="600" mb="4px">
    {children}
  </Text>
);

export const PageHeader = ({ title, subtitle, right }: { title: string; subtitle: string; right?: React.ReactNode }) => (
  <Flex align="center" justify="space-between" mb="24px" flexWrap="wrap" gap="12px">
    <Flex align="center" gap="12px">
      <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
      <Box>
        <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
          {title}
        </Text>
        <Text color={crm.textMuted} fontSize="sm" mt="2px">
          {subtitle}
        </Text>
      </Box>
    </Flex>
    {right}
  </Flex>
);

export const tableHeadStyle = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: "12px",
  color: crm.textMuted,
  fontWeight: 600,
  borderBottom: `1px solid ${crm.border}`,
  whiteSpace: "nowrap",
  background: crm.subtle,
} as React.CSSProperties;

export const tableCellStyle = {
  padding: "10px 12px",
  fontSize: "14px",
  color: crm.text,
  borderBottom: `1px solid ${crm.border}`,
} as React.CSSProperties;
